import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { renderWithMobileBreaks, renderWithDualBreaks } from "../utils/mobileBreaks";

const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

export const PhilosophyContrast = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language.startsWith("zh");
  const [ref, visible] = useReveal();

  return (
    <section
      ref={ref}
      className="py-24 md:py-32 bg-[var(--bg-paper)]"
      id="philosophy"
      data-testid="philosophy-section"
    >
      <div className="max-w-5xl mx-auto px-6">
        <div className={`text-center mb-12 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <p className="text-[11px] uppercase tracking-[0.42em] text-[var(--wax)] mb-4">
            {t("philosophy.kicker")}
          </p>
          <h2 className={`${isZh ? "font-cn" : "font-display"} text-3xl md:text-4xl tracking-tight`} data-testid="philosophy-title">
            {renderWithMobileBreaks(t("philosophy.title"))}
          </h2>
        </div>

        {/* Old literary-journal frame */}
        <div
          className={`border border-[var(--rule)] bg-[var(--bg-paper-deep)]/40 transition-all duration-1000 delay-150 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
          style={{
            boxShadow:
              "0 22px 50px -22px rgba(28,26,23,0.35), inset 0 0 0 1px rgba(138,58,51,0.06)",
          }}
        >
          <div className="grid md:grid-cols-12">
            {/* Question column */}
            <aside
              className="md:col-span-5 p-8 md:p-12 md:border-r border-b md:border-b-0 border-dashed border-[var(--rule)] relative"
              data-testid="philosophy-question-column"
            >
              <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--ink-mute)] mb-6">
                {t("philosophy.marginalia_q")}
              </div>
              <blockquote
                className={`${isZh ? "font-cn" : "font-display"} font-semibold text-[20px] md:text-[28px] leading-[1.55] md:leading-[1.5] text-[var(--ink-soft)] italic`}
                data-testid="philosophy-question"
              >
                {renderWithDualBreaks(t("philosophy.question"))}
              </blockquote>

              {/* Old-handbook page number */}
              <div className="hidden md:block absolute bottom-6 left-12 text-[10px] uppercase tracking-[0.4em] text-[var(--ink-mute)]">
                — i —
              </div>
            </aside>

            {/* Answer column — handwritten letter feel */}
            <article
              className="md:col-span-7 p-8 md:p-12"
              data-testid="philosophy-answer-column"
            >
              <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--wax)] mb-6">
                {t("philosophy.marginalia_a")}
              </div>

              <p
                className={`${isZh ? "font-cn" : "font-en"} text-[20px] md:text-[22px] leading-snug text-[var(--ink)] mb-5`}
                data-testid="philosophy-answer-intro"
              >
                {t("philosophy.answer_intro")}
              </p>

              <div className={`${isZh ? "font-cn md:text-[16px]" : "font-en md:text-[17px]"} space-y-5 text-[16px] leading-[1.95] text-[var(--ink)] [text-wrap:pretty]`}>
                <p data-testid="philosophy-answer-p1">{renderWithDualBreaks(t("philosophy.answer_p1"))}</p>
                <p data-testid="philosophy-answer-p2">{renderWithDualBreaks(t("philosophy.answer_p2"))}</p>
                <p data-testid="philosophy-answer-p3">{renderWithDualBreaks(t("philosophy.answer_p3"))}</p>
              </div>

              <div className="mt-10 pt-5 border-t border-dashed border-[var(--rule)] flex items-center justify-between">
                <span
                  className={`${isZh ? "font-cn" : "font-display"} italic text-[var(--ink-soft)] text-[14px]`}
                  data-testid="philosophy-signoff"
                >
                  {t("philosophy.signoff")}
                </span>
                <img
                  src="/press-kit/logo-192.png"
                  alt="MiProse M·P monogram wax seal"
                  data-testid="philosophy-mp-seal"
                  width="44"
                  height="44"
                  draggable={false}
                  style={{
                    width: 44,
                    height: 44,
                    objectFit: "contain",
                    imageRendering: "-webkit-optimize-contrast",
                    mixBlendMode: "multiply",
                  }}
                />
              </div>

              {/* page number */}
              <div className="hidden md:block mt-4 text-right text-[10px] uppercase tracking-[0.4em] text-[var(--ink-mute)]">
                — ii —
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
};
